/**
 * QuickPatientSearch — global top-bar patient search (Ctrl+P also focuses it via /patients).
 * Searches by name or full 10-digit mobile (indexed blind-index search) and opens the patient window.
 */
import { useState } from 'react';
import {
  Dialog, DialogContent, DialogTitle, IconButton, List, ListItemButton, ListItemText,
  TextField, Tooltip, Typography, CircularProgress, Box,
} from '@mui/material';
import { Search } from 'lucide-react';
import { api } from '../../lib/api';
import { usePatientDetails } from '../../contexts/PatientDetailsContext';
import { normalizeArray } from '../../lib/utils';
import type { Patient } from '../../types';
import { useTranslation } from 'react-i18next';

export function QuickPatientSearch() {
  const { t } = useTranslation('ui');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const { openPatient } = usePatientDetails();

  const runSearch = async () => {
    const q = query.trim();
    if (q.length < 2) return;
    setLoading(true);
    setSearched(true);
    try {
      const res = await api.patients.search(q);
      setResults(normalizeArray<Patient>(res.data || []));
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const select = (p: Patient) => {
    setOpen(false);
    setQuery('');
    setResults([]);
    setSearched(false);
    openPatient(p.id, p);
  };

  const digits = query.replace(/\D/g, '');
  const partialNumber = digits.length > 0 && digits.length === query.replace(/\s/g, '').length && digits.length < 10;

  return (
    <>
      <Tooltip title={t('findPatientNameOrFull10')}>
        <IconButton size="small" onClick={() => setOpen(true)} sx={{ color: 'inherit' }}>
          <Search size={18} />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', pb: 0.5 }}>{t('findPatient')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={t('nameOrFull10DigitMobile')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
            helperText={partialNumber
              ? 'Mobile search needs the full 10-digit number — or search by name'
              : 'Press Enter to search'}
            sx={{ mt: 1 }}
          />
          {loading && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={22} /></Box>}
          {!loading && results.length > 0 && (
            <List dense>
              {results.map((p) => (
                <ListItemButton key={p.id} onClick={() => select(p)}>
                  <ListItemText
                    primary={p.name}
                    secondary={[p.age != null ? `${p.age}y` : null, p.gender, p.mobile].filter(Boolean).join(' · ')}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
          {!loading && searched && results.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1.5, textAlign: 'center' }}>
              {t('noMatchingPatientFound')}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
